import {
  BASE_URL,
  deleteRequest,
  fetchFile,
  get,
  IdNameTransport,
  post,
  put,
} from "./Common";
import { Portfolio } from "./PortfolioApi";

export interface L4 {
  l4Id: number;
  name: string;
  numberInL3: number;
}

export interface LifestyleTemplateSelection {
  id: number;
  name: string;
}

export interface L3 {
  l3Id: number;
  size: number;
  inSameRoomEnabled: boolean;
  name: string;
  maxNumberOfMatches: number;
  lifestyleTemplateSelection?: LifestyleTemplateSelection;
  renewalRenewalMatchingAllowed: boolean;
  renewalRenewalFindingAllowed: boolean;
  newNewMatchingAllowed: boolean;
  newNewFindingAllowed: boolean;
  forcedClosed: boolean;
  l4: null | L4[];
}

export interface IExtendedL3 extends L3 {
  l2Id: number;
  l2name: string;
  matchingRoundStatus: string | null;
  key?: string;
  current?: number;
}

type MatchingText = {
  confirmMessage: string;
  confirmNotification: string;
};

interface ReminderEmails {
  daysBefore: number;
  enabled: boolean;
}

export interface EntrataReminderEmails {
  enabled: boolean;
  twentyOneDayReminder: boolean;
  tenDayReminder: boolean;
  threeDayReminder: boolean;
}

export interface IGender {
  id: number;
  name: string;
  groupId: number;
}

export interface IGenderGroups {
  groupCanMatchAll: boolean;
  groupCanViewAll: boolean;
  id: number;
  name: string;
}

export interface IGenderNetworkSettingsTransport {
  genderSelectionCanHide: boolean;
  genderSelectionEnabled: boolean;
  genderSelectionVisibleToOthers: boolean;
  genderSelectionExplainer: string;
  genderSelectionTitle: string;
  genders: IGender[];
  genderGroups: IGenderGroups[];
}

export interface Network {
  l1Id: number;
  l2Id: number;
  l1Name: string;
  l2Name: string;
  personalProNounEnabled: boolean;
  userPhoneNumbersEnabled: boolean;
  coedEnabled: boolean;
  passCodeEnabled: boolean;
  passCode: number;
  importType: string | null;
  smokingEnabled: boolean;
  genderNetworkSettingsTransport: IGenderNetworkSettingsTransport;
  enabled: boolean;
  bioEnabled: boolean;
  petEnabled: boolean;
  housingEnabled: boolean;
  genderEnabled: boolean;
  transgenderEnabled: boolean;
  transgenderOption: string | null;
  apt: boolean;
  activationStartDate: string;
  activationEndDate: string;
  matchingStartDate: string;
  matchingEndDate: string;
  matchingRoundStatus: string | null;
  l3: L3[];
  l3CurrrentMatches?: Record<number, number>;
  usersInNetwork: number;
  portfolio?: Portfolio;
  placementWorksheetEnabled: boolean;
  postMatchingMatchesVisible: boolean;
  previouslyKnownEnabled: boolean;
  matchingText: MatchingText;
  reminderEmails: ReminderEmails[] | null;
  entrataReminderEmails: EntrataReminderEmails | null;
}

export interface GetAllNetworksResponse {
  networks: Network[];
}

export interface NetworkCreationRequest {
  networkName: undefined | string;
  portfolioType?: undefined | string;
  portfolio?: undefined | string;
  subnetworkName: undefined | string;
  l1Desc: undefined | string;
  importType?: undefined | string | null;
  apt: undefined | boolean;
  unitTypeName: undefined | string;
  unitSize: undefined | number;
  matchingStartDate: null | string;
  matchingEndDate: null | string;
  activationStartDate: null | string;
  activationEndDate: null | string;
  enabled?: null | boolean;
  bioEnabled?: null | boolean;
}

export interface SubnetworkCreationRequest {
  subnetworkName: undefined | string;
  unitTypeName: undefined | string;
  unitSize: undefined | number;
  matchingStartDate: null | string;
  matchingEndDate: null | string;
  activationStartDate: null | string;
  activationEndDate: null | string;
}

export interface UnitCreationRequest {
  unitTypeName: undefined | string;
  unitSize: undefined | number;
  inSameRoom: undefined | boolean;
  templateId: undefined | number;
}

export interface RoomCreationRequest {
  roomName: undefined | string;
  numberInL3: undefined | number;
}

export interface L2CloneRequest {
  l1IdSource: number;
  l2IdSource: number;
  subNetworkName: string;
  seasonId: undefined | string;
  grouping: undefined | string;
  matchingStartDate: undefined | string;
  matchingEndDate: undefined | string;
  activationStartDate: undefined | string;
  activationEndDate: undefined | string;
}

export interface L3Clone {
  l3Id: number;
  name: string;
}

export interface L3CloneRequest {
  requests: L3Clone[];
}

export interface MatchingRoundObject {
  l1Id: string;
  l2Id: string;
}

export interface L2MergeRequest {
  name: string;
  matchingRoundsToBeMerged: MatchingRoundObject[];
  settingsSource: MatchingRoundObject | null;
  matchingStartDate: null | number[];
  matchingEndDate: null | number[];
  activationStartDate: null | number[];
  activationEndDate: null | number[];
}

export const GetAllNetworkInfo = async (
  networkIds: number[] = [],
  authToken: string
) => {
  let url = `${BASE_URL}/networks`;

  if (networkIds.length > 0) {
    url = `${url}?networkIds=${networkIds.join(",")}`;
  }

  const httpResponse = await get<GetAllNetworksResponse>(url, authToken);

  return httpResponse.parsedBody as GetAllNetworksResponse;
};

export interface UpdateNotificationsRequest {
  matchingText: MatchingText;
  reminderEmails: ReminderEmails[];
  entrataReminderEmails: EntrataReminderEmails;
}

interface UpdateNotificationsResponse {}

export const UpdateUserNotifications = async (
  l1Id: number,
  l2Id: number,
  payload: UpdateNotificationsRequest,
  authToken: string
) => {
  const url = `${BASE_URL}/networks/${l1Id}/subnetwork/${l2Id}/user/notifications`;

  const response = await post<UpdateNotificationsResponse>(
    url,
    authToken,
    new Map<string, string | number | boolean>(),
    payload
  );

  return response;
};

export const SearchNetworkByName = async (
  name: string,
  authToken: string,
  ids?: number[]
) => {
  const url = `${BASE_URL}/networks/search`;

  const queryParams = new Map<string, string | number | boolean>();

  queryParams.set("filter", name);

  if (ids) {
    queryParams.set("idsToInclude", ids.join(","));
  }

  const response = await get<IdNameTransport[]>(url, authToken, queryParams);

  return response.parsedBody;
};

export const LoadAllNetworkSettings = async (authToken: string) => {
  const url = `${BASE_URL}/networks/report.csv`;

  return await fetchFile(url, authToken);
};

export const CreateNetwork = async (
  payload: NetworkCreationRequest,
  authToken: string
) => {
  const url = `${BASE_URL}/networks`;

  const httpResponse = await post<GetAllNetworksResponse>(
    url,
    authToken,
    new Map<string, string | number | boolean>(),
    payload
  );

  return httpResponse.parsedBody;
};

export const CreateSubnetwork = async (
  l1Id: number,
  payload: SubnetworkCreationRequest,
  authToken: string
) => {
  const url = `${BASE_URL}/networks/${l1Id}/subnetworks`;

  const httpResponse = await post<GetAllNetworksResponse>(
    url,
    authToken,
    new Map<string, string | number | boolean>(),
    payload
  );

  return httpResponse.parsedBody;
};

export const UpdateNetwork = async (
  l1Id: number,
  payload: Pick<
    NetworkCreationRequest,
    "networkName" | "importType" | "portfolioType" | "enabled" | "bioEnabled"
  >,
  authToken: string
) => {
  const url = `${BASE_URL}/networks/${l1Id}`;

  const httpResponse = await put<GetAllNetworksResponse>(
    url,
    authToken,
    new Map<string, string | number | boolean>(),
    payload
  );

  return httpResponse.parsedBody;
};

export const RenameSubnetwork = async (
  l1Id: number,
  l2Id: number,
  payload: Pick<SubnetworkCreationRequest, "subnetworkName">,
  authToken: string
) => {
  const url = `${BASE_URL}/networks/${l1Id}/subnetworks/${l2Id}`;

  const httpResponse = await put<GetAllNetworksResponse>(
    url,
    authToken,
    new Map<string, string | number | boolean>(),
    payload
  );

  return httpResponse.parsedBody;
};

export const DeleteUnitType = async (
    l1Id: number,
    l2Id: number,
    l3Id: number,
    authToken: string
) => {
  const url = `${BASE_URL}/networks/${l1Id}/subnetworks/${l2Id}/units/${l3Id}`;

  const httpResponse = await deleteRequest(
      url,
      authToken
  );

  return httpResponse.parsedBody;
};

export const RenameUnitType = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  payload: Pick<UnitCreationRequest, "unitTypeName">,
  authToken: string
) => {
  const url = `${BASE_URL}/networks/${l1Id}/subnetworks/${l2Id}/units/${l3Id}`;

  const httpResponse = await put<GetAllNetworksResponse>(
    url,
    authToken,
    new Map<string, string | number | boolean>(),
    payload
  );

  return httpResponse.parsedBody;
};

export const UpdateSizeUnitType = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  payload: Pick<UnitCreationRequest, "unitSize">,
  authToken: string
) => {
  const url = `${BASE_URL}/networks/${l1Id}/subnetworks/${l2Id}/units/${l3Id}`;

  const httpResponse = await put<GetAllNetworksResponse>(
    url,
    authToken,
    new Map<string, string | number | boolean>(),
    payload
  );

  return httpResponse.parsedBody;
};

export const UpdateInSameRoomUnitType = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  payload: Pick<UnitCreationRequest, "inSameRoom">,
  authToken: string
) => {
  const url = `${BASE_URL}/networks/${l1Id}/subnetworks/${l2Id}/units/${l3Id}`;

  const httpResponse = await put<GetAllNetworksResponse>(
    url,
    authToken,
    new Map<string, string | number | boolean>(),
    payload
  );

  return httpResponse.parsedBody;
};

export const DeleteRoom = async (
    l1Id: number,
    l2Id: number,
    l3Id: number,
    l4Id: number,
    authToken: string
) => {
  const url = `${BASE_URL}/networks/${l1Id}/subnetworks/${l2Id}/units/${l3Id}/rooms/${l4Id}`;

  const response = await deleteRequest(url, authToken);

  return response.parsedBody;
};

export const RenameRoom = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  l4Id: number,
  payload: Pick<RoomCreationRequest, "roomName">,
  authToken: string
) => {
  const url = `${BASE_URL}/networks/${l1Id}/subnetworks/${l2Id}/units/${l3Id}/rooms/${l4Id}`;

  const httpResponse = await put<GetAllNetworksResponse>(
    url,
    authToken,
    new Map<string, string | number | boolean>(),
    payload
  );

  return httpResponse.parsedBody;
};

export const UpdateNumberInL3 = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  l4Id: number,
  payload: Pick<RoomCreationRequest, "numberInL3">,
  authToken: string
) => {
  const url = `${BASE_URL}/networks/${l1Id}/subnetworks/${l2Id}/units/${l3Id}/rooms/${l4Id}`;

  const httpResponse = await put<GetAllNetworksResponse>(
    url,
    authToken,
    new Map<string, string | number | boolean>(),
    payload
  );

  return httpResponse.parsedBody;
};

export const CreateUnit = async (
  l1Id: number,
  l2Id: number,
  payload: UnitCreationRequest,
  authToken: string
) => {
  const url = `${BASE_URL}/networks/${l1Id}/subnetworks/${l2Id}/units`;

  const httpResponse = await post<GetAllNetworksResponse>(
    url,
    authToken,
    new Map<string, string | number | boolean>(),
    payload
  );

  return httpResponse.parsedBody;
};

export const CreateRoom = async (
  l1Id: number,
  l2Id: number,
  l3Id: number,
  payload: RoomCreationRequest,
  authToken: string
) => {
  const url = `${BASE_URL}/networks/${l1Id}/subnetworks/${l2Id}/units/${l3Id}/rooms`;

  const httpResponse = await post<GetAllNetworksResponse>(
    url,
    authToken,
    new Map<string, string | number | boolean>(),
    payload
  );

  return httpResponse.parsedBody;
};

export const CancelInvitation = async (
  invitationId: string,
  authToken: string
) => {
  const url = `${BASE_URL}/invitations/${invitationId}`;
  await deleteRequest(url, authToken);
};

export const ResendInvitation = async (
  invitationId: string,
  authToken: string
) => {
  const url = `${BASE_URL}/invitations/${invitationId}/resend`;

  await post(url, authToken);
};

export interface Invitation {
  invitationType: string;
  createdAt: string;
  updatedAt: string;
  l1Id: number;
  l2Id: number;
  l3Id: number;
  l4Id: number;
  uid: string;
  id: string;
  claimedAt: string;
  claimedBy: string;
  cancelledAt: string;
  expiresAt: string;
}

export const GetInvitationForNetwork = async (
  l1Id: number,
  authToken: string
) => {
  const url = `${BASE_URL}/invitations/network/${l1Id}/invitations_url?invitationType=GENERAL`;
  const invitationResponseHttpResponse = await get<Record<string, any>>(
    url,
    authToken
  );

  if (invitationResponseHttpResponse.status !== 200) {
  } else {
    return invitationResponseHttpResponse.parsedBody;
  }
};

export const CreateInvitation = async (
  invitationType: string,
  l1Id: number,
  l2Id: number,
  l3Id: number,
  l4Id: number,
  uid: string | null,
  renewal: boolean,
  authToken: string
) => {
  const url = `${BASE_URL}/invitations/network/${l1Id}/invitations`;

  const invitationResponse = await post<Invitation>(
    url,
    authToken,
    new Map<string, string | number | boolean>(),
    {
      invitationType,
      l1Id,
      l2Id,
      l3Id,
      l4Id,
      uid,
      renewal,
    }
  );

  if (invitationResponse.status !== 200) {
    return null;
  }
  return invitationResponse.parsedBody;
};

export const ResetInvitations = async (
  invitationId: string,
  authToken: string
) => {
  const url = `${BASE_URL}/invitations/${invitationId}/reset`;
  return post(url, authToken);
};

export const CloneL2 = async (request: L2CloneRequest, authToken: string) => {
  const url = `${BASE_URL}/networks/${request.l1IdSource}/clone`;

  return post(url, authToken, undefined, request);
};

export const CloneL3 = async (l1Id: number, l2Id: number, request: L3CloneRequest, authToken: string) => {
  const url = `${BASE_URL}/networks/${l1Id}/matching_round/${l2Id}/clone`;

  return post(url, authToken, undefined, request);
};

export const MergeL2 = async (request: L2MergeRequest, l1Id: Number, authToken: string) => {
  const url = `${BASE_URL}/networks/${l1Id}/merge`;

  return post(url, authToken, undefined, request);
};
